import * as PIXI from 'pixi.js';
import * as PIXIFilters from 'pixi-filters';
import { CONSTANTS, IMAGE_ELEMENT_CONFIG } from '../../PreviewDisplayConstants';
import { calculateShadowFilterPadding } from '../../helpers';
import { getMediaSourceSingleUrl } from '../../../helpers';

export default class ImageElement {

    constructor(container, onTextureLoaded) {
        this.container = container;
        this.onTextureLoaded = onTextureLoaded;

        this.sprite = new PIXI.Sprite();
        this.sprite.anchor.set(0.5, 0.5);
        this.container.addChild(this.sprite);

        this.shadowFilter = new PIXIFilters.DropShadowFilter({
            quality: IMAGE_ELEMENT_CONFIG.SHADOW_QUALITY
        });

        this.imageUrl = null;
        this.config = null;

        // The texture the sprite is waiting on, and the callback waiting with it. Held so that both a
        // swapped picture and a destroyed element can take the callback back off.
        this.pendingTextureLoad = null;
    }

    // Base units map onto the renderer's pixel grid by how much taller the preview is than the frame
    // the settings are authored against. Positions, sizes and shadow offsets all scale by it.
    calculateBaseUnitMultiplier() {
        return CONSTANTS.DEFAULT_HEIGHT / IMAGE_ELEMENT_CONFIG.BASE_HEIGHT;
    }

    update(config, stackingOrder) {
        this.config = config;

        this.sprite.visible = config.visible !== false;
        this.sprite.zIndex = stackingOrder;

        this.loadTexture(config.mediaSource);
        this.applyShadow(config.shadowSettings);
        this.applyTransform(config);
    }

    loadTexture(mediaSource) {
        const imageUrl = getMediaSourceSingleUrl(mediaSource);

        if (this.imageUrl === imageUrl)
            return;

        // The user may have swapped the picture while the previous one was still downloading.
        this.cancelPendingTextureLoad();
        this.imageUrl = imageUrl;

        if (!imageUrl) {
            this.sprite.texture = PIXI.Texture.EMPTY;
            return;
        }

        this.sprite.texture = PIXI.Texture.from(imageUrl);
        this.enableMipmaps(this.sprite.texture.baseTexture);

        // A texture already in the PIXI cache is valid straight away and needs no reflow.
        if (this.sprite.texture.baseTexture.valid)
            return;

        this.waitForTextureLoad(this.sprite.texture.baseTexture);
    }

    // Lays out the picture once its file has arrived, and asks for a redraw: a paused preview would
    // otherwise stay empty until something else happened to repaint it.
    waitForTextureLoad(baseTexture) {
        const onLoaded = () => {
            this.pendingTextureLoad = null;
            this.applyTransform(this.config);
            this.onTextureLoaded();
        };

        this.pendingTextureLoad = { baseTexture: baseTexture, onLoaded: onLoaded };
        baseTexture.once('loaded', onLoaded);
    }

    // Without this the callback outlives the element: PIXI keeps the base texture in its own cache,
    // so it still fires, and by then destroy() has taken the sprite's texture away and laying the
    // picture out again would read it off nothing.
    cancelPendingTextureLoad() {
        if (!this.pendingTextureLoad)
            return;

        const { baseTexture, onLoaded } = this.pendingTextureLoad;

        baseTexture.off('loaded', onLoaded);
        this.pendingTextureLoad = null;
    }

    // Stock photos arrive around 1080px wide and are drawn at a fraction of that, so without mip
    // levels the minification aliases and the picture reads as pixelated. PIXI's default builds them
    // only for power of two textures, which a photo never is. ON covers the rest under WebGL 2 and
    // PIXI itself falls back to plain linear filtering where mip levels are not available.
    enableMipmaps(baseTexture) {
        if (baseTexture.mipmap === PIXI.MIPMAP_MODES.ON)
            return;

        baseTexture.setStyle(undefined, PIXI.MIPMAP_MODES.ON);

        // A style change on its own never reaches the GPU: PIXI re-reads the style only while it is
        // re-uploading the texture data. A texture served from the cache is already uploaded, so it
        // needs the nudge; one still downloading picks the style up on its first upload.
        if (baseTexture.valid)
            baseTexture.update();
    }

    applyTransform(config) {
        this.moveTo(config.position);
        this.fitIntoBox(config.sizeFactor);
    }

    // Separate from applyTransform so a canvas drag can move the sprite on its own, without a
    // round trip through the store, and still land on the same pixel the stored position means.
    moveTo(position) {
        const multiplier = this.calculateBaseUnitMultiplier();

        this.sprite.position.set(position.x * multiplier, position.y * multiplier);
    }

    // Every image is first scaled to fit inside the same square box, so a portrait photo and a wide
    // banner start out equally sized, and the Size slider grows or shrinks it from there. The factor
    // applies to both axes at once: the picture is never stretched out of its own proportions.
    fitIntoBox(sizeFactor) {
        if (!this.sprite.texture.baseTexture.valid)
            return;

        // Images added before the size control existed keep their fitted size.
        const scale = this.calculateFittedScale() * (sizeFactor ?? 1);

        this.sprite.scale.set(scale, scale);
    }

    // The inverse of fitIntoBox: the factor the size controls would have to read for the picture to
    // be drawn at the size it is on screen right now. A corner resize scales the sprite directly and
    // writes to the store only on release, so the stored factor lags behind for the whole drag and
    // the sprite is the only place the current size can be read from.
    getSizeFactor() {
        if (!this.sprite.texture.baseTexture.valid)
            return this.config?.sizeFactor ?? 1;

        return this.sprite.scale.x / this.calculateFittedScale();
    }

    // The picture's natural pixel size, or null while its texture is still downloading. Everything
    // that has to reason about how much of the frame the picture can cover needs it.
    getNaturalSize() {
        const texture = this.sprite.texture;

        if (!texture.baseTexture.valid)
            return null;

        return { width: texture.width, height: texture.height };
    }

    calculateFittedScale() {
        const texture = this.sprite.texture;
        const boxSize = IMAGE_ELEMENT_CONFIG.BOX_SIZE * this.calculateBaseUnitMultiplier();

        return Math.min(boxSize / texture.width, boxSize / texture.height);
    }

    applyShadow(shadowSettings) {
        const shadowEnabled = Boolean(shadowSettings && shadowSettings.enabled);

        if (!shadowEnabled) {
            // Detaching the filter keeps PIXI on the plain sprite path instead of allocating a
            // render texture per image for a shadow nobody asked for.
            if (this.sprite.filters)
                this.sprite.filters = null;

            return;
        }

        const multiplier = this.calculateBaseUnitMultiplier();
        const scaledDistance = shadowSettings.distance * multiplier;

        this.shadowFilter.blur = shadowSettings.blur * multiplier;
        this.shadowFilter.alpha = shadowSettings.opacity;
        this.shadowFilter.color = shadowSettings.color;
        this.shadowFilter.distance = scaledDistance;
        this.shadowFilter.rotation = shadowSettings.angle;

        // Last, because every one of the assignments above puts back the filter's own too small
        // measurement of the region it needs.
        this.shadowFilter.padding = calculateShadowFilterPadding(this.shadowFilter.kernels, scaledDistance);

        if (!this.sprite.filters)
            this.sprite.filters = [this.shadowFilter];
    }

    destroy() {
        this.cancelPendingTextureLoad();
        this.sprite.filters = null;
        this.shadowFilter.destroy();
        this.sprite.destroy();
    }
}
